/**
 * Medication Management Service
 * Provides the medication domain model for MedFast Aged Care.
 *
 * Covers:
 *  - Medication Orders (prescriptions lifecycle)
 *  - MAR  (Medication Administration Records)
 *  - S8   (Schedule 8 controlled substance register)
 *  - Drug Safety (allergy, duplicate therapy, polypharmacy)
 *
 * Storage keys are isolated from the legacy agedcare_* keys used by
 * S8RegisterService / AdverseDrugReactionService so both layers can coexist
 * until the migration is complete.
 */

import LocalStorageService from '../storage/LocalStorageService';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const LOG_PREFIX = '[MedicationService]';

const STORAGE_KEYS = {
  ORDERS: 'mf_medication_orders_v1',
  MAR: 'mf_mar_records_v1',
  S8: 'mf_s8_register_v1',
  ADR: 'mf_adverse_reactions_v1',
};

export const ORDER_TYPE = Object.freeze({
  LONG_TERM: 'long_term',
  SHORT_TERM: 'short_term',
  STAT: 'stat',
  PRN: 'prn',
  S8: 's8',
});

export const ORDER_STATUS = Object.freeze({
  ACTIVE: 'active',
  CEASED: 'ceased',
  WITHHELD: 'withheld',
  ON_HOLD: 'on_hold',
});

export const MAR_STATUS = Object.freeze({
  GIVEN: 'given',
  WITHHELD: 'withheld',
  REFUSED: 'refused',
  SELF_ADMINISTERED: 'self_administered',
  NOT_AVAILABLE: 'not_available',
});

export const S8_TRANSACTION_TYPE = Object.freeze({
  RECEIVED: 'received',
  ADMINISTERED: 'administered',
  WASTED: 'wasted',
  RETURNED: 'returned',
  DISCREPANCY: 'discrepancy',
});

/** ATC prefixes that indicate psychotropic / antipsychotic medications. */
const PSYCHOTROPIC_ATC_PREFIXES = Object.freeze({
  N05A: { label: 'Antipsychotics', psychotropic: true, antipsychotic: true },
  N05B: { label: 'Anxiolytics', psychotropic: true, antipsychotic: false },
  N05C: { label: 'Hypnotics/Sedatives', psychotropic: true, antipsychotic: false },
});

/** N06A is monitored but NOT auto-flagged as psychotropic. */
const MONITORED_ATC_PREFIXES = Object.freeze({
  N06A: { label: 'Antidepressants' },
});

const POLYPHARMACY_THRESHOLD = 9;

// Shift time boundaries (24-h clock)
const SHIFT_BOUNDARIES = Object.freeze({
  AM: { start: 6, end: 14 },
  PM: { start: 14, end: 22 },
  NIGHT: { start: 22, end: 6 },
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isSameDay(dateA, dateB) {
  const a = new Date(dateA);
  const b = new Date(dateB);
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isWithinDateRange(dateStr, startDate, endDate) {
  const d = new Date(dateStr);
  if (startDate && d < new Date(startDate)) return false;
  if (endDate && d > new Date(endDate)) return false;
  return true;
}

function hourInShift(hour, shift) {
  const b = SHIFT_BOUNDARIES[shift];
  if (!b) return true;
  if (b.start < b.end) return hour >= b.start && hour < b.end;
  // Wraps midnight (NIGHT)
  return hour >= b.start || hour < b.end;
}

function classifyAtcCode(atcCode) {
  if (!atcCode) return { isPsychotropic: false, isAntipsychotic: false, isMonitored: false };

  const upper = atcCode.toUpperCase();
  for (const [prefix, meta] of Object.entries(PSYCHOTROPIC_ATC_PREFIXES)) {
    if (upper.startsWith(prefix)) {
      return {
        isPsychotropic: meta.psychotropic,
        isAntipsychotic: meta.antipsychotic,
        isMonitored: true,
      };
    }
  }
  for (const prefix of Object.keys(MONITORED_ATC_PREFIXES)) {
    if (upper.startsWith(prefix)) {
      return { isPsychotropic: false, isAntipsychotic: false, isMonitored: true };
    }
  }
  return { isPsychotropic: false, isAntipsychotic: false, isMonitored: false };
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

class MedicationService {
  constructor() {
    this._ensureStorageKeys();
  }

  _ensureStorageKeys() {
    Object.values(STORAGE_KEYS).forEach((key) => {
      if (!LocalStorageService.get(key)) {
        LocalStorageService.set(key, []);
      }
    });
  }

  // =========================================================================
  // Medication Orders
  // =========================================================================

  createOrder(data) {
    try {
      const atcFlags = classifyAtcCode(data.atcCode);
      const now = new Date().toISOString();

      const order = {
        id: data.id || LocalStorageService.generateId(),
        residentId: data.residentId,
        drugName: data.drugName || '',
        genericName: data.genericName || '',
        atcCode: data.atcCode || '',
        strength: data.strength || '',
        dose: data.dose || '',
        route: data.route || '',
        frequency: data.frequency || '',
        orderType: data.orderType || ORDER_TYPE.LONG_TERM,
        prescriberId: data.prescriberId || null,
        prescriberName: data.prescriberName || '',
        startDate: data.startDate || now,
        endDate: data.endDate || null,
        instructions: data.instructions || '',
        indication: data.indication || '',
        isPsychotropic: atcFlags.isPsychotropic,
        isAntipsychotic: atcFlags.isAntipsychotic,
        status: data.status || ORDER_STATUS.ACTIVE,
        createdAt: now,
        updatedAt: now,
      };

      const created = LocalStorageService.addItem(STORAGE_KEYS.ORDERS, order);

      LocalStorageService.logAudit({
        action_type: 'CREATE',
        record_type: 'MedicationOrder',
        record_id: created.id,
        new_value: created,
        user_id: data.prescriberId || 'system',
        metadata: {
          drugName: order.drugName,
          orderType: order.orderType,
          isPsychotropic: order.isPsychotropic,
        },
      });

      console.debug(`${LOG_PREFIX} Order created: ${order.drugName} [${order.orderType}] for resident ${order.residentId}`);
      return { success: true, data: created };
    } catch (error) {
      console.error(`${LOG_PREFIX} createOrder failed:`, error);
      return { success: false, error: error.message };
    }
  }

  getOrdersByResident(residentId, filters = {}) {
    try {
      let orders = LocalStorageService.find(
        STORAGE_KEYS.ORDERS,
        (o) => o.residentId === residentId,
      );

      if (filters.status) {
        const statusList = Array.isArray(filters.status) ? filters.status : [filters.status];
        orders = orders.filter((o) => statusList.includes(o.status));
      }
      if (filters.orderType) {
        const typeList = Array.isArray(filters.orderType) ? filters.orderType : [filters.orderType];
        orders = orders.filter((o) => typeList.includes(o.orderType));
      }

      orders.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
      console.debug(`${LOG_PREFIX} getOrdersByResident(${residentId}) → ${orders.length} orders`);
      return { success: true, data: orders };
    } catch (error) {
      console.error(`${LOG_PREFIX} getOrdersByResident failed:`, error);
      return { success: false, error: error.message };
    }
  }

  ceaseOrder(orderId, reason, ceasedBy) {
    try {
      const existing = LocalStorageService.findById(STORAGE_KEYS.ORDERS, orderId);
      if (!existing) return { success: false, error: 'Order not found' };

      const updated = LocalStorageService.updateItem(STORAGE_KEYS.ORDERS, orderId, {
        status: ORDER_STATUS.CEASED,
        ceasedReason: reason,
        ceasedBy,
        ceasedAt: new Date().toISOString(),
      });

      LocalStorageService.logAudit({
        action_type: 'UPDATE',
        record_type: 'MedicationOrder',
        record_id: orderId,
        previous_value: existing,
        new_value: updated,
        user_id: ceasedBy,
        metadata: { action: 'cease', reason },
      });

      console.debug(`${LOG_PREFIX} Order ceased: ${orderId} — ${reason}`);
      return { success: true, data: updated };
    } catch (error) {
      console.error(`${LOG_PREFIX} ceaseOrder failed:`, error);
      return { success: false, error: error.message };
    }
  }

  holdOrder(orderId, reason, heldBy) {
    try {
      const existing = LocalStorageService.findById(STORAGE_KEYS.ORDERS, orderId);
      if (!existing) return { success: false, error: 'Order not found' };

      const updated = LocalStorageService.updateItem(STORAGE_KEYS.ORDERS, orderId, {
        status: ORDER_STATUS.ON_HOLD,
        holdReason: reason,
        heldBy,
        heldAt: new Date().toISOString(),
      });

      LocalStorageService.logAudit({
        action_type: 'UPDATE',
        record_type: 'MedicationOrder',
        record_id: orderId,
        previous_value: existing,
        new_value: updated,
        user_id: heldBy,
        metadata: { action: 'hold', reason },
      });

      console.debug(`${LOG_PREFIX} Order held: ${orderId}`);
      return { success: true, data: updated };
    } catch (error) {
      console.error(`${LOG_PREFIX} holdOrder failed:`, error);
      return { success: false, error: error.message };
    }
  }

  resumeOrder(orderId, resumedBy) {
    try {
      const existing = LocalStorageService.findById(STORAGE_KEYS.ORDERS, orderId);
      if (!existing) return { success: false, error: 'Order not found' };
      if (existing.status !== ORDER_STATUS.ON_HOLD) {
        return { success: false, error: 'Only on-hold orders can be resumed' };
      }

      const updated = LocalStorageService.updateItem(STORAGE_KEYS.ORDERS, orderId, {
        status: ORDER_STATUS.ACTIVE,
        resumedBy,
        resumedAt: new Date().toISOString(),
        holdReason: null,
        heldBy: null,
        heldAt: null,
      });

      LocalStorageService.logAudit({
        action_type: 'UPDATE',
        record_type: 'MedicationOrder',
        record_id: orderId,
        previous_value: existing,
        new_value: updated,
        user_id: resumedBy,
        metadata: { action: 'resume' },
      });

      console.debug(`${LOG_PREFIX} Order resumed: ${orderId}`);
      return { success: true, data: updated };
    } catch (error) {
      console.error(`${LOG_PREFIX} resumeOrder failed:`, error);
      return { success: false, error: error.message };
    }
  }

  getActiveOrders(residentId) {
    return this.getOrdersByResident(residentId, { status: ORDER_STATUS.ACTIVE });
  }

  getPolypharmacyAlert(residentId) {
    try {
      const { data: active } = this.getActiveOrders(residentId);
      if (!active) return { success: true, data: null };

      if (active.length >= POLYPHARMACY_THRESHOLD) {
        const alert = {
          type: 'polypharmacy',
          severity: 'warning',
          residentId,
          activeCount: active.length,
          threshold: POLYPHARMACY_THRESHOLD,
          message: `Polypharmacy alert: ${active.length} active medications (threshold ${POLYPHARMACY_THRESHOLD}). Review recommended.`,
          medications: active.map((o) => ({ id: o.id, drugName: o.drugName, atcCode: o.atcCode })),
          generatedAt: new Date().toISOString(),
        };
        console.debug(`${LOG_PREFIX} Polypharmacy alert for resident ${residentId}: ${active.length} meds`);
        return { success: true, data: alert };
      }
      return { success: true, data: null };
    } catch (error) {
      console.error(`${LOG_PREFIX} getPolypharmacyAlert failed:`, error);
      return { success: false, error: error.message };
    }
  }

  getPsychotropicOrders(residentId) {
    try {
      const orders = LocalStorageService.find(
        STORAGE_KEYS.ORDERS,
        (o) => o.residentId === residentId && o.isPsychotropic && o.status === ORDER_STATUS.ACTIVE,
      );
      console.debug(`${LOG_PREFIX} getPsychotropicOrders(${residentId}) → ${orders.length}`);
      return { success: true, data: orders };
    } catch (error) {
      console.error(`${LOG_PREFIX} getPsychotropicOrders failed:`, error);
      return { success: false, error: error.message };
    }
  }

  // =========================================================================
  // MAR — Medication Administration Records
  // =========================================================================

  recordAdministration(data) {
    try {
      if (!data.medicationOrderId || !data.residentId) {
        return { success: false, error: 'medicationOrderId and residentId are required' };
      }

      const now = new Date().toISOString();
      const record = {
        id: data.id || LocalStorageService.generateId(),
        medicationOrderId: data.medicationOrderId,
        residentId: data.residentId,
        scheduledTime: data.scheduledTime || now,
        actualTime: data.actualTime || now,
        status: data.status || MAR_STATUS.GIVEN,
        administeredBy: data.administeredBy || null,
        witnessedBy: data.witnessedBy || null,
        notes: data.notes || '',
        prnReason: data.prnReason || null,
        prnEffectivenessReviewAt: data.prnReason
          ? new Date(Date.now() + 60 * 60 * 1000).toISOString()
          : null,
        prnEffectivenessScore: null,
        createdAt: now,
      };

      const created = LocalStorageService.addItem(STORAGE_KEYS.MAR, record);

      LocalStorageService.logAudit({
        action_type: 'CREATE',
        record_type: 'MARRecord',
        record_id: created.id,
        new_value: created,
        user_id: data.administeredBy || 'system',
        metadata: {
          status: record.status,
          medicationOrderId: record.medicationOrderId,
          witnessedBy: record.witnessedBy,
        },
      });

      console.debug(`${LOG_PREFIX} MAR recorded: order=${record.medicationOrderId} status=${record.status}`);
      return { success: true, data: created };
    } catch (error) {
      console.error(`${LOG_PREFIX} recordAdministration failed:`, error);
      return { success: false, error: error.message };
    }
  }

  getMARByResident(residentId, date) {
    try {
      const records = LocalStorageService.find(
        STORAGE_KEYS.MAR,
        (r) => r.residentId === residentId && isSameDay(r.scheduledTime, date),
      );
      records.sort((a, b) => new Date(a.scheduledTime) - new Date(b.scheduledTime));
      console.debug(`${LOG_PREFIX} getMARByResident(${residentId}, ${date}) → ${records.length}`);
      return { success: true, data: records };
    } catch (error) {
      console.error(`${LOG_PREFIX} getMARByResident failed:`, error);
      return { success: false, error: error.message };
    }
  }

  getMARByShift(residentId, date, shift) {
    try {
      const shiftUpper = (shift || '').toUpperCase();
      const records = LocalStorageService.find(
        STORAGE_KEYS.MAR,
        (r) => {
          if (r.residentId !== residentId) return false;
          if (!isSameDay(r.scheduledTime, date)) return false;
          const hour = new Date(r.scheduledTime).getHours();
          return hourInShift(hour, shiftUpper);
        },
      );
      records.sort((a, b) => new Date(a.scheduledTime) - new Date(b.scheduledTime));
      console.debug(`${LOG_PREFIX} getMARByShift(${residentId}, ${date}, ${shift}) → ${records.length}`);
      return { success: true, data: records };
    } catch (error) {
      console.error(`${LOG_PREFIX} getMARByShift failed:`, error);
      return { success: false, error: error.message };
    }
  }

  getPendingAdministrations(residentId) {
    try {
      const { data: activeOrders } = this.getActiveOrders(residentId);
      if (!activeOrders) return { success: true, data: [] };

      const today = new Date().toISOString();
      const todayRecords = LocalStorageService.find(
        STORAGE_KEYS.MAR,
        (r) => r.residentId === residentId && isSameDay(r.scheduledTime, today),
      );

      const administeredOrderIds = new Set(todayRecords.map((r) => r.medicationOrderId));
      const pending = activeOrders.filter((o) => !administeredOrderIds.has(o.id));

      console.debug(`${LOG_PREFIX} getPendingAdministrations(${residentId}) → ${pending.length} pending`);
      return { success: true, data: pending };
    } catch (error) {
      console.error(`${LOG_PREFIX} getPendingAdministrations failed:`, error);
      return { success: false, error: error.message };
    }
  }

  getAdministrationStats(residentId, dateRange = {}) {
    try {
      const { startDate, endDate } = dateRange;
      const records = LocalStorageService.find(
        STORAGE_KEYS.MAR,
        (r) =>
          r.residentId === residentId &&
          isWithinDateRange(r.scheduledTime, startDate, endDate),
      );

      const total = records.length;
      const given = records.filter((r) => r.status === MAR_STATUS.GIVEN).length;
      const selfAdmin = records.filter((r) => r.status === MAR_STATUS.SELF_ADMINISTERED).length;
      const withheld = records.filter((r) => r.status === MAR_STATUS.WITHHELD).length;
      const refused = records.filter((r) => r.status === MAR_STATUS.REFUSED).length;
      const notAvailable = records.filter((r) => r.status === MAR_STATUS.NOT_AVAILABLE).length;
      const administered = given + selfAdmin;
      const complianceRate = total > 0 ? Math.round((administered / total) * 10000) / 100 : 100;

      const stats = {
        total,
        given,
        selfAdministered: selfAdmin,
        withheld,
        refused,
        notAvailable,
        complianceRate,
        missedDoses: total - administered,
        dateRange: { startDate: startDate || null, endDate: endDate || null },
      };

      console.debug(`${LOG_PREFIX} getAdministrationStats(${residentId}) → compliance ${complianceRate}%`);
      return { success: true, data: stats };
    } catch (error) {
      console.error(`${LOG_PREFIX} getAdministrationStats failed:`, error);
      return { success: false, error: error.message };
    }
  }

  recordPRNEffectiveness(marId, score, notes) {
    try {
      const existing = LocalStorageService.findById(STORAGE_KEYS.MAR, marId);
      if (!existing) return { success: false, error: 'MAR record not found' };
      if (!existing.prnReason) {
        return { success: false, error: 'This MAR record is not a PRN administration' };
      }

      const updated = LocalStorageService.updateItem(STORAGE_KEYS.MAR, marId, {
        prnEffectivenessScore: score,
        prnEffectivenessNotes: notes || '',
        prnEffectivenessReviewedAt: new Date().toISOString(),
      });

      LocalStorageService.logAudit({
        action_type: 'UPDATE',
        record_type: 'MARRecord',
        record_id: marId,
        previous_value: existing,
        new_value: updated,
        user_id: existing.administeredBy || 'system',
        metadata: { action: 'prn_effectiveness', score },
      });

      console.debug(`${LOG_PREFIX} PRN effectiveness recorded: ${marId} score=${score}`);
      return { success: true, data: updated };
    } catch (error) {
      console.error(`${LOG_PREFIX} recordPRNEffectiveness failed:`, error);
      return { success: false, error: error.message };
    }
  }

  // =========================================================================
  // S8 Register — Schedule 8 Controlled Substances
  // =========================================================================

  recordS8Transaction(data) {
    try {
      const requiresDualWitness = [
        S8_TRANSACTION_TYPE.ADMINISTERED,
        S8_TRANSACTION_TYPE.WASTED,
      ];
      if (requiresDualWitness.includes(data.transactionType)) {
        if (!data.witnessedBy) {
          return { success: false, error: 'Dual witness required for administered/wasted S8 transactions' };
        }
        if (data.performedBy === data.witnessedBy) {
          return { success: false, error: 'Witness must be a different person from the performer' };
        }
      }
      if (!data.performedBy) {
        return { success: false, error: 'performedBy is required' };
      }

      const currentBalance = this._getS8Balance(data.medicationOrderId);
      const delta = this._s8Delta(data.transactionType, data.quantity || 0);
      const balanceAfter = currentBalance + delta;

      if (balanceAfter < 0) {
        return { success: false, error: `Insufficient S8 balance: current ${currentBalance}, attempted ${data.quantity}` };
      }

      const now = new Date().toISOString();
      const entry = {
        id: data.id || LocalStorageService.generateId(),
        medicationOrderId: data.medicationOrderId,
        residentId: data.residentId || null,
        transactionType: data.transactionType,
        quantity: data.quantity || 0,
        balanceBefore: currentBalance,
        balanceAfter,
        performedBy: data.performedBy,
        witnessedBy: data.witnessedBy || null,
        notes: data.notes || '',
        timestamp: now,
        createdAt: now,
      };

      const created = LocalStorageService.addItem(STORAGE_KEYS.S8, entry);

      LocalStorageService.logAudit({
        action_type: 'CREATE',
        record_type: 'S8Register',
        record_id: created.id,
        new_value: created,
        user_id: data.performedBy,
        metadata: {
          transactionType: entry.transactionType,
          quantity: entry.quantity,
          balanceBefore: entry.balanceBefore,
          balanceAfter: entry.balanceAfter,
          witnessedBy: entry.witnessedBy,
        },
      });

      console.debug(
        `${LOG_PREFIX} S8 transaction: ${entry.transactionType} qty=${entry.quantity} ` +
        `balance ${entry.balanceBefore}→${entry.balanceAfter} order=${entry.medicationOrderId}`,
      );
      return { success: true, data: created };
    } catch (error) {
      console.error(`${LOG_PREFIX} recordS8Transaction failed:`, error);
      return { success: false, error: error.message };
    }
  }

  getS8Balance(medicationOrderId) {
    try {
      const balance = this._getS8Balance(medicationOrderId);
      return { success: true, data: { medicationOrderId, balance } };
    } catch (error) {
      console.error(`${LOG_PREFIX} getS8Balance failed:`, error);
      return { success: false, error: error.message };
    }
  }

  getS8Register(facilityId, dateRange = {}) {
    try {
      const { startDate, endDate } = dateRange;
      const all = LocalStorageService.get(STORAGE_KEYS.S8, []);
      let entries = all;

      if (facilityId) {
        entries = entries.filter((e) => e.facilityId === facilityId);
      }
      if (startDate || endDate) {
        entries = entries.filter((e) => isWithinDateRange(e.timestamp, startDate, endDate));
      }

      entries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      console.debug(`${LOG_PREFIX} getS8Register → ${entries.length} entries`);
      return { success: true, data: entries };
    } catch (error) {
      console.error(`${LOG_PREFIX} getS8Register failed:`, error);
      return { success: false, error: error.message };
    }
  }

  checkS8Discrepancy(medicationOrderId) {
    try {
      const entries = LocalStorageService.find(
        STORAGE_KEYS.S8,
        (e) => e.medicationOrderId === medicationOrderId,
      );
      if (entries.length === 0) {
        return { success: true, data: { valid: true, message: 'No S8 records found' } };
      }

      entries.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      const errors = [];
      for (let i = 1; i < entries.length; i++) {
        const prev = entries[i - 1];
        const curr = entries[i];
        if (curr.balanceBefore !== prev.balanceAfter) {
          errors.push({
            index: i,
            entryId: curr.id,
            expected: prev.balanceAfter,
            actual: curr.balanceBefore,
            timestamp: curr.timestamp,
          });
        }
      }

      const result = {
        valid: errors.length === 0,
        totalEntries: entries.length,
        currentBalance: entries[entries.length - 1].balanceAfter,
        discrepancies: errors,
      };

      if (errors.length > 0) {
        console.warn(`${LOG_PREFIX} S8 discrepancy found for order ${medicationOrderId}: ${errors.length} chain breaks`);
      }
      return { success: true, data: result };
    } catch (error) {
      console.error(`${LOG_PREFIX} checkS8Discrepancy failed:`, error);
      return { success: false, error: error.message };
    }
  }

  getS8AuditReport(facilityId) {
    try {
      const { data: entries } = this.getS8Register(facilityId);
      if (!entries) return { success: false, error: 'Could not load S8 register' };

      const byOrder = {};
      entries.forEach((e) => {
        const key = e.medicationOrderId || 'unlinked';
        if (!byOrder[key]) byOrder[key] = [];
        byOrder[key].push(e);
      });

      const summaries = Object.entries(byOrder).map(([orderId, orderEntries]) => {
        const sorted = orderEntries.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        const latest = sorted[sorted.length - 1];
        const received = orderEntries.filter((e) => e.transactionType === S8_TRANSACTION_TYPE.RECEIVED);
        const administered = orderEntries.filter((e) => e.transactionType === S8_TRANSACTION_TYPE.ADMINISTERED);
        const wasted = orderEntries.filter((e) => e.transactionType === S8_TRANSACTION_TYPE.WASTED);
        const returned = orderEntries.filter((e) => e.transactionType === S8_TRANSACTION_TYPE.RETURNED);
        const discrepancies = orderEntries.filter((e) => e.transactionType === S8_TRANSACTION_TYPE.DISCREPANCY);

        return {
          medicationOrderId: orderId,
          currentBalance: latest.balanceAfter,
          totalReceived: received.reduce((s, e) => s + e.quantity, 0),
          totalAdministered: administered.reduce((s, e) => s + e.quantity, 0),
          totalWasted: wasted.reduce((s, e) => s + e.quantity, 0),
          totalReturned: returned.reduce((s, e) => s + e.quantity, 0),
          discrepancyCount: discrepancies.length,
          transactionCount: orderEntries.length,
          firstTransaction: sorted[0].timestamp,
          lastTransaction: latest.timestamp,
        };
      });

      const report = {
        facilityId,
        generatedAt: new Date().toISOString(),
        totalEntries: entries.length,
        uniqueOrders: summaries.length,
        totalDiscrepancies: summaries.reduce((s, r) => s + r.discrepancyCount, 0),
        orders: summaries,
      };

      console.debug(`${LOG_PREFIX} S8 audit report: ${report.totalEntries} entries, ${report.uniqueOrders} orders`);
      return { success: true, data: report };
    } catch (error) {
      console.error(`${LOG_PREFIX} getS8AuditReport failed:`, error);
      return { success: false, error: error.message };
    }
  }

  /** Private: compute running balance from the chain. */
  _getS8Balance(medicationOrderId) {
    const entries = LocalStorageService.find(
      STORAGE_KEYS.S8,
      (e) => e.medicationOrderId === medicationOrderId,
    );
    if (entries.length === 0) return 0;
    const sorted = entries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    return sorted[0].balanceAfter ?? 0;
  }

  _s8Delta(transactionType, quantity) {
    const directions = {
      [S8_TRANSACTION_TYPE.RECEIVED]: 1,
      [S8_TRANSACTION_TYPE.ADMINISTERED]: -1,
      [S8_TRANSACTION_TYPE.WASTED]: -1,
      [S8_TRANSACTION_TYPE.RETURNED]: -1,
      [S8_TRANSACTION_TYPE.DISCREPANCY]: 0,
    };
    return (directions[transactionType] ?? 0) * quantity;
  }

  // =========================================================================
  // Drug Safety
  // =========================================================================

  /**
   * Check the resident's known allergies against a proposed drug name.
   * Loads allergy data from the `agedcare_residents` collection in
   * LocalStorageService (populated from mockData).
   */
  checkAllergyInteraction(residentId, drugName) {
    try {
      const residents = LocalStorageService.get('agedcare_residents', []);
      const resident = residents.find((r) => r.id === residentId);
      if (!resident) return { success: true, data: { hasInteraction: false, warnings: [] } };

      const allergies = resident.clinicalProfile?.allergies
        || resident.allergies
        || [];

      const drugLower = (drugName || '').toLowerCase();
      const warnings = [];

      allergies.forEach((allergy) => {
        const allergen = (allergy.allergen || allergy).toString().toLowerCase();
        if (drugLower.includes(allergen) || allergen.includes(drugLower)) {
          warnings.push({
            type: 'allergy',
            severity: allergy.severity || 'unknown',
            allergen: allergy.allergen || allergy,
            reaction: allergy.reaction || 'Unknown reaction',
            message: `Patient has a known ${allergy.severity || ''} allergy to ${allergy.allergen || allergy}: ${allergy.reaction || 'reaction details unavailable'}`,
          });
        }
      });

      return {
        success: true,
        data: {
          hasInteraction: warnings.length > 0,
          warnings,
        },
      };
    } catch (error) {
      console.error(`${LOG_PREFIX} checkAllergyInteraction failed:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Checks whether an active order with the same ATC code prefix (first 5 chars)
   * already exists, indicating duplicate therapy.
   */
  checkDuplicateTherapy(residentId, atcCode) {
    try {
      if (!atcCode) return { success: true, data: { hasDuplicate: false, duplicates: [] } };

      const classPrefix = atcCode.substring(0, 5).toUpperCase();
      const duplicates = LocalStorageService.find(
        STORAGE_KEYS.ORDERS,
        (o) =>
          o.residentId === residentId &&
          o.status === ORDER_STATUS.ACTIVE &&
          o.atcCode &&
          o.atcCode.substring(0, 5).toUpperCase() === classPrefix,
      );

      return {
        success: true,
        data: {
          hasDuplicate: duplicates.length > 0,
          duplicates: duplicates.map((d) => ({
            id: d.id,
            drugName: d.drugName,
            atcCode: d.atcCode,
            dose: d.dose,
            startDate: d.startDate,
          })),
        },
      };
    } catch (error) {
      console.error(`${LOG_PREFIX} checkDuplicateTherapy failed:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Runs all safety checks for a proposed new order. Returns a consolidated
   * result with `safe`, `warnings[]`, and `blocks[]`.
   */
  runSafetyChecks(residentId, newOrder) {
    try {
      const warnings = [];
      const blocks = [];

      // 1) Allergy check
      const allergyResult = this.checkAllergyInteraction(residentId, newOrder.drugName);
      if (allergyResult.success && allergyResult.data.hasInteraction) {
        allergyResult.data.warnings.forEach((w) => {
          if (w.severity === 'severe') {
            blocks.push(w);
          } else {
            warnings.push(w);
          }
        });
      }

      // 2) Duplicate therapy check
      const dupResult = this.checkDuplicateTherapy(residentId, newOrder.atcCode);
      if (dupResult.success && dupResult.data.hasDuplicate) {
        warnings.push({
          type: 'duplicate_therapy',
          severity: 'warning',
          message: `Duplicate therapy detected: ${dupResult.data.duplicates.length} active order(s) in the same ATC class (${newOrder.atcCode?.substring(0, 5)})`,
          duplicates: dupResult.data.duplicates,
        });
      }

      // 3) Polypharmacy check
      const polyResult = this.getPolypharmacyAlert(residentId);
      if (polyResult.success && polyResult.data) {
        warnings.push({
          type: 'polypharmacy',
          severity: 'warning',
          message: polyResult.data.message,
          activeCount: polyResult.data.activeCount,
        });
      }

      // 4) Psychotropic flag
      const atcFlags = classifyAtcCode(newOrder.atcCode);
      if (atcFlags.isPsychotropic) {
        warnings.push({
          type: 'psychotropic',
          severity: 'info',
          message: `This medication is classified as psychotropic (ATC ${newOrder.atcCode}). Ensure appropriate consent and monitoring plans are in place.`,
          isAntipsychotic: atcFlags.isAntipsychotic,
        });
      }

      const safe = blocks.length === 0;

      console.debug(
        `${LOG_PREFIX} Safety checks for resident ${residentId}: ` +
        `safe=${safe} warnings=${warnings.length} blocks=${blocks.length}`,
      );

      return {
        success: true,
        data: { safe, warnings, blocks },
      };
    } catch (error) {
      console.error(`${LOG_PREFIX} runSafetyChecks failed:`, error);
      return { success: false, error: error.message };
    }
  }
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export { MedicationService };
const medicationServiceInstance = new MedicationService();
export default medicationServiceInstance;
